// Repository pattern for IndexedDB operations
// Provides typed, centralized access to different entity types

import { db, cacheStore, imageStore, metadataStore, syncQueueStore } from './database.js';

/**
 * Base repository with common operations
 */
class BaseRepository {
  constructor(store, entityType) {
    this.store = store;
    this.entityType = entityType;
  }

  /**
   * Generate cache key for entity
   */
  generateKey(id) {
    return `${this.entityType}:${id}`;
  }

  /**
   * Get entity by ID
   */
  async getById(id) {
    const key = this.generateKey(id);
    return await db.get(key, this.store);
  }

  /**
   * Save entity with metadata
   */
  async save(id, data, options = {}) {
    const key = this.generateKey(id);
    const now = Date.now();
    
    const entry = {
      data,
      metadata: {
        entityType: this.entityType,
        id,
        createdAt: options.createdAt || now,
        updatedAt: now,
        expiresAt: options.ttl ? now + options.ttl : null,
        lastAccessedAt: now,
        // Sync fields for future sync support
        lastSyncedAt: now,
        localUpdatedAt: now,
        syncStatus: 'synced',
        deletedLocally: false,
      },
    };

    return await db.set(key, entry, this.store);
  }

  /**
   * Update existing entity
   */
  async update(id, updates) {
    const existing = await this.getById(id);
    if (!existing) return null;

    const now = Date.now();
    existing.data = { ...existing.data, ...updates };
    existing.metadata.updatedAt = now;
    existing.metadata.localUpdatedAt = now;
    existing.metadata.syncStatus = 'dirty'; // Mark for future sync

    return await db.set(this.generateKey(id), existing, this.store);
  }

  /**
   * Delete entity (soft delete for sync support)
   */
  async delete(id) {
    const existing = await this.getById(id);
    if (!existing) return false;

    // Soft delete - mark as deleted locally for sync
    existing.metadata.deletedLocally = true;
    existing.metadata.syncStatus = 'dirty';
    existing.metadata.localUpdatedAt = Date.now();

    return await db.set(this.generateKey(id), existing, this.store);
  }

  /**
   * Hard delete (remove from database)
   */
  async hardDelete(id) {
    return await db.delete(this.generateKey(id), this.store);
  }

  /**
   * Get all entities of this type
   */
  async getAll() {
    const allKeys = await db.getKeys(this.store);
    const entityKeys = allKeys.filter(key => key.startsWith(`${this.entityType}:`));
    
    const entities = await Promise.all(
      entityKeys.map(async (key) => {
        const entry = await db.get(key, this.store);
        // Filter out soft-deleted entities
        if (entry?.metadata?.deletedLocally) return null;
        return entry;
      })
    );

    return entities.filter(Boolean);
  }

  /**
   * Clear all entities of this type
   */
  async clear() {
    const allKeys = await db.getKeys(this.store);
    const entityKeys = allKeys.filter(key => key.startsWith(`${this.entityType}:`));
    
    await Promise.all(
      entityKeys.map(key => db.delete(key, this.store))
    );

    return true;
  }

  /**
   * Get expired entities
   */
  async getExpired() {
    const allKeys = await db.getKeys(this.store);
    const entityKeys = allKeys.filter(key => key.startsWith(`${this.entityType}:`));
    const now = Date.now();
    
    const expired = await Promise.all(
      entityKeys.map(async (key) => {
        const entry = await db.get(key, this.store);
        if (entry?.metadata?.expiresAt && entry.metadata.expiresAt < now) {
          return key;
        }
        return null;
      })
    );

    return expired.filter(Boolean);
  }

  /**
   * Clean up expired entities
   */
  async cleanupExpired() {
    const expiredKeys = await this.getExpired();
    await Promise.all(
      expiredKeys.map(key => db.delete(key, this.store))
    );
    return expiredKeys.length;
  }
}

/**
 * Cache metadata repository
 */
class CacheMetadataRepository extends BaseRepository {
  constructor() {
    super(metadataStore, 'cache_metadata');
  }

  /**
   * Track cache access
   */
  async trackAccess(cacheKey) {
    const existing = await this.getById(cacheKey);
    const now = Date.now();

    if (existing) {
      existing.metadata.lastAccessedAt = now;
      await db.set(this.generateKey(cacheKey), existing, this.store);
    } else {
      await this.save(cacheKey, { cacheKey }, { createdAt: now });
    }
  }

  /**
   * Get cache statistics by entity type
   */
  async getStatsByType(entityType) {
    const all = await this.getAll();
    const typed = all.filter(entry => entry.data?.entityType === entityType);
    
    return {
      count: typed.length,
      totalSize: JSON.stringify(typed).length,
      oldestAccess: typed.length > 0 
        ? Math.min(...typed.map(e => e.metadata.lastAccessedAt))
        : null,
    };
  }
}

/**
 * Image repository with blob storage
 */
class ImageRepository extends BaseRepository {
  constructor() {
    super(imageStore, 'image');
  }

  /**
   * Generate image key from URL
   */
  generateKey(url) {
    // Create a hash-like key from URL
    const hash = this.simpleHash(url);
    return `image:${hash}`;
  }

  /**
   * Simple hash function for URLs
   */
  simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash;
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Save image blob
   */
  async saveImage(url, blob, metadata = {}) {
    const key = this.generateKey(url);
    const now = Date.now();

    const entry = {
      url,
      blob,
      metadata: {
        ...metadata,
        entityType: 'image',
        createdAt: now,
        updatedAt: now,
        expiresAt: metadata.ttl ? now + metadata.ttl : null,
        lastAccessedAt: now,
        size: blob.size,
        type: blob.type,
      },
    };

    return await db.set(key, entry, this.store);
  }

  /**
   * Get image by URL
   */
  async getImage(url) {
    const key = this.generateKey(url);
    const entry = await db.get(key, this.store);
    
    if (entry) {
      // Update access time
      entry.metadata.lastAccessedAt = Date.now();
      await db.set(key, entry, this.store);
    }

    return entry;
  }

  /**
   * Check if image is cached
   */
  async isCached(url) {
    const entry = await this.getImage(url);
    return !!entry;
  }

  /**
   * Get total storage used by images
   */
  async getTotalSize() {
    const allKeys = await db.getKeys(this.store);
    const imageKeys = allKeys.filter(key => key.startsWith('image:'));
    
    let totalSize = 0;
    for (const key of imageKeys) {
      const entry = await db.get(key, this.store);
      if (entry?.metadata?.size) {
        totalSize += entry.metadata.size;
      }
    }

    return totalSize;
  }

  /**
   * Clean up old images based on LRU
   */
  async cleanupOldImages(maxSizeBytes = 50 * 1024 * 1024) {
    let currentSize = await this.getTotalSize();
    
    if (currentSize <= maxSizeBytes) return 0;

    const allKeys = await db.getKeys(this.store);
    const imageKeys = allKeys.filter(key => key.startsWith('image:'));
    
    // Get all images with access times
    const images = await Promise.all(
      imageKeys.map(async (key) => {
        const entry = await db.get(key, this.store);
        return { key, lastAccessed: entry?.metadata?.lastAccessedAt || 0 };
      })
    );

    // Sort by last accessed (oldest first)
    images.sort((a, b) => a.lastAccessed - b.lastAccessed);

    // Delete oldest until under limit
    let deletedCount = 0;
    for (const { key } of images) {
      if (currentSize <= maxSizeBytes) break;
      
      const entry = await db.get(key, this.store);
      if (entry?.metadata?.size) {
        await db.delete(key, this.store);
        currentSize -= entry.metadata.size;
        deletedCount++;
      }
    }

    return deletedCount;
  }
}

/**
 * Sync queue repository for future sync implementation
 */
class SyncQueueRepository extends BaseRepository {
  constructor() {
    super(syncQueueStore, 'sync_queue');
  }

  /**
   * Use raw store key for sync queue entries.
   */
  generateKey(id) {
    return id;
  }

  /**
   * Get queue entry by raw key.
   */
  async getById(id) {
    return await db.get(id, this.store);
  }

  /**
   * Get all queue keys.
   */
  async getKeys() {
    return await db.getKeys(this.store);
  }

  /**
   * Update queue entry by raw key.
   */
  async update(id, updates) {
    const existing = await this.getById(id);
    if (!existing) return null;

    const now = Date.now();
    existing.data = { ...existing.data, ...updates };
    existing.metadata = { ...existing.metadata, updatedAt: now };

    return await db.set(id, existing, this.store);
  }

  /**
   * Add mutation to queue
   * Persists { data, metadata } so mutation-queue getPending / isDuplicate / update work.
   * Key must equal mutation.mutationId (used by updateStatus / remove).
   */
  async enqueue(mutation) {
    const key = mutation.mutationId;
    if (!key) {
      throw new Error('[SyncQueueRepository] mutationId is required');
    }

    const now = Date.now();
    const status = mutation.status || 'pending';

    const entry = {
      id: key,
      data: {
        mutationId: key,
        entityType: mutation.entityType,
        entityId: mutation.entityId,
        operationType: mutation.operationType || mutation.operation,
        payload: mutation.payload ?? {},
        idempotencyKey: mutation.idempotencyKey,
        createdAt: mutation.createdAt ?? now,
        retryCount: mutation.retryCount ?? 0,
        lastAttemptAt: mutation.lastAttemptAt ?? null,
        status,
        dependencies: mutation.dependencies || [],
        url: mutation.url,
        method: mutation.method,
        headers: mutation.headers,
        metadata: mutation.metadata || {},
      },
      metadata: {
        entityType: 'sync_mutation',
        id: key,
        createdAt: now,
        updatedAt: now,
        timestamp: now,
        retryCount: 0,
        status,
      },
    };

    const ok = await db.set(key, entry, this.store);
    if (!ok) {
      throw new Error(`[SyncQueueRepository] Failed to persist mutation ${key}`);
    }
    return entry;
  }

  /**
   * Update mutation status
   */
  async updateStatus(id, status, retryCount) {
    const key = id;
    const entry = await db.get(key, this.store);
    
    if (entry) {
      entry.metadata.status = status;
      entry.metadata.updatedAt = Date.now();
      if (retryCount !== undefined) {
        entry.metadata.retryCount = retryCount;
      }
      if (entry.data) {
        entry.data.status = status;
        if (retryCount !== undefined) {
          entry.data.retryCount = retryCount;
        }
      }
      await db.set(key, entry, this.store);
    }

    return entry;
  }

  /**
   * Remove mutation from queue
   */
  async remove(id) {
    return await db.delete(id, this.store);
  }

  /**
   * Get the next sequence number for ordering mutations.
   * Atomically increments a counter stored in IndexedDB so sequence numbers
   * survive page reloads and are unique across the lifetime of the app.
   */
  async getNextSequence() {
    const counterKey = '_meta:syncSequence';
    const entry = await db.get(counterKey, this.store);
    const current = entry?.value ?? 0;
    const next = current + 1;
    await db.set(counterKey, { value: next, updatedAt: Date.now() }, this.store);
    return next;
  }

  /**
   * Get the current highest sequence number (without incrementing).
   */
  async getCurrentSequence() {
    const counterKey = '_meta:syncSequence';
    const entry = await db.get(counterKey, this.store);
    return entry?.value ?? 0;
  }

  /**
   * Clear all pending mutations
   */
  async clearPending() {
    const allKeys = await db.getKeys(this.store);
    const mutationKeys = allKeys.filter(key => key.startsWith('mutation:'));
    await Promise.all(
      mutationKeys.map(key => this.remove(key))
    );
    return mutationKeys.length;
  }
}

/**
 * ── Shared Image URL Registry ──────────────────────────────────────────────
 * Module-level singleton shared across ALL EntityRepository instances so
 * athletesRepository, trainersRepository, etc. never overwrite each other's
 * entries in IndexedDB.
 */
let _imageUrlRegistry = {};          // { "entityType:entityId": { currentUrl, additionalUrls, previousUrl, updatedAt } }
let _urlRegistryLoaded = false;

async function _loadUrlRegistry() {
  if (_urlRegistryLoaded) return;
  try {
    const stored = await db.get('_imageUrlRegistry', metadataStore);
    if (stored && stored.registry) {
      _imageUrlRegistry = stored.registry;
    }
    _urlRegistryLoaded = true;
  } catch (e) {
    console.warn('[EntityRepo] Failed to load image URL registry:', e.message);
    _urlRegistryLoaded = true;
  }
}

async function _persistUrlRegistry() {
  try {
    await db.set(
      '_imageUrlRegistry',
      { registry: _imageUrlRegistry, updatedAt: Date.now() },
      metadataStore
    );
  } catch (e) {
    console.warn('[EntityRepo] Failed to persist image URL registry:', e.message);
  }
}

/** Simple hash matching ImageRepository.simpleHash */
function _simpleHash(str) {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    const char = str.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash;
  }
  return Math.abs(hash).toString(36);
}

/**
 * Invalidate (delete) a cached image blob from the image store & Cache API
 */
async function _invalidateImage(imageUrl) {
  if (!imageUrl) return;
  const key = `image:${_simpleHash(imageUrl)}`;
  try {
    await db.delete(key, imageStore);
    if (typeof caches !== 'undefined') {
      const cache = await caches.open('gym-disposable-images');
      await cache.delete(imageUrl);
    }
    console.log('[EntityRepo] Invalidated cached image:', imageUrl.slice(0, 60));
  } catch (e) {
    console.warn('[EntityRepo] Failed to invalidate image:', e.message);
  }
}

/**
 * Pre-fetch and cache an image blob in the background
 */
async function _prefetchAndCacheImage(imageUrl) {
  if (!imageUrl) return;
  try {
    const response = await fetch(imageUrl);
    if (!response.ok) return;
    const blob = await response.blob();
    const now = Date.now();
    const key = `image:${_simpleHash(imageUrl)}`;
    const entry = {
      url: imageUrl,
      blob,
      metadata: {
        entityType: 'image',
        createdAt: now,
        updatedAt: now,
        expiresAt: now + 30 * 24 * 60 * 60 * 1000, // 30 days
        lastAccessedAt: now,
        size: blob.size,
        type: blob.type,
        source: 'entity-sync',
      },
    };
    await db.set(key, entry, imageStore);
    console.log('[EntityRepo] Pre-cached image:', imageUrl.slice(0, 60));
  } catch (e) {
    console.warn('[EntityRepo] Pre-cache failed for image:', imageUrl.slice(0, 60), e.message);
  }
}

/**
 * Generic entity repository for API responses
 */
class EntityRepository extends BaseRepository {
  constructor(entityType) {
    super(cacheStore, entityType);
  }

  /**
   * Extract image URLs from entity data and check for changes.
   * Checks top-level fields AND recursively scans nested entity wrappers
   * (data.athlete, data.trainer, data.profile, data.gym, data.user)
   * and array fields (data.athletes[], data.trainers[], etc.)
   *
   * This is necessary because server endpoints wrap entities inside keys:
   *   { athlete: { profileImage: "..." } } → profileImage is NOT at top level
   *   { athletes: [{ profileImage: "..." }, ...] } → must scan the array
   */
  async _syncEntityImageUrls(entityType, entityId, data) {
    if (!data || typeof data !== 'object') return;

    await _loadUrlRegistry();

    // Collect all image URLs (use Set to deduplicate)
    const imageUrls = new Set();

    // Recursively walk an object/array looking for image-related fields.
    // This catches images at ANY nesting depth — flat, single-entity wrappers
    // (data.athlete), nested arrays (data.dashboardData.athlete.overdueathletes[]),
    // and any other structure the server might return.
    const MAX_DEPTH = 6; // safety limit
    const _deepCollect = (obj, depth = 0) => {
      if (!obj || typeof obj !== 'object' || depth > MAX_DEPTH) return;
      if (obj instanceof Blob) return; // skip binary data

      // Check image-carrying fields on this object
      if (obj.profileImage && typeof obj.profileImage === 'string') {
        imageUrls.add(obj.profileImage);
      }
      if (Array.isArray(obj.images)) {
        for (const url of obj.images) {
          if (url && typeof url === 'string') imageUrls.add(url);
        }
      }
      if (obj.avatar && typeof obj.avatar === 'string') {
        imageUrls.add(obj.avatar);
      }

      // Recurse into values
      if (Array.isArray(obj)) {
        for (const item of obj) {
          _deepCollect(item, depth + 1);
        }
      } else {
        for (const key of Object.keys(obj)) {
          _deepCollect(obj[key], depth + 1);
        }
      }
    };

    // Start recursive collection from the root
    _deepCollect(data);

    console.log(`[EntityRepo] _syncEntityImageUrls: entityType=${entityType} entityId=${String(entityId ?? '').slice(0, 20)} urlsFound=${imageUrls.size} topKeys=${Object.keys(data).join(',')}`);
    if (imageUrls.size === 0) return;

    const urlsArray = [...imageUrls];
    const registryKey = `${entityType}:${String(entityId ?? '')}`;
    const prevEntry = _imageUrlRegistry[registryKey];
    const prevUrls = prevEntry
      ? [...new Set([prevEntry.currentUrl, ...(prevEntry.additionalUrls || [])])]
      : [];

    // Check each current image URL against what we previously tracked
    for (const currentUrl of urlsArray) {
      if (prevUrls.includes(currentUrl)) {
        // URL is unchanged — skip (no network needed)
        continue;
      }

      // New or changed URL — invalidate stale cached blob if there was an old one
      if (prevEntry && prevEntry.currentUrl && prevEntry.currentUrl !== currentUrl) {
        _invalidateImage(prevEntry.currentUrl).catch(() => {});
      }

      // Pre-cache the new image as a fire-and-forget background task
      _prefetchAndCacheImage(currentUrl).catch(() => {});
    }

    // Update registry with current image URLs
    const primaryUrl = urlsArray[0] || null;
    const additionalUrls = urlsArray.slice(1);
    _imageUrlRegistry[registryKey] = {
      currentUrl: primaryUrl,
      additionalUrls: additionalUrls.length > 0 ? additionalUrls : undefined,
      previousUrl: prevEntry?.currentUrl || null,
      updatedAt: Date.now(),
    };

    // Persist asynchronously (fire-and-forget)
    _persistUrlRegistry().catch(() => {});
  }

  /**
   * Save API response with cache policy
   */
  async saveResponse(id, data, options = {}) {
    const result = await this.save(id, data, {
      ttl: options.ttl,
      createdAt: options.createdAt,
    });

    // After saving, detect image URL changes and pre-cache new images
    // This runs asynchronously and won't block the save operation.
    if (data && typeof data === 'object') {
      const entityId = id.replace(`${this.entityType}:`, '').split('?')[0];
      this._syncEntityImageUrls(this.entityType, entityId, data).catch(() => {});
    }

    return result;
  }

  /**
   * Get cached response if still valid
   */
  async getCachedResponse(id) {
    const entry = await this.getById(id);
    
    if (!entry) return null;

    // Check if expired
    if (entry.metadata.expiresAt && entry.metadata.expiresAt < Date.now()) {
      await this.hardDelete(id);
      return null;
    }

    return entry;
  }

  /**
   * Check if response is stale
   */
  async isStale(id, staleTime = 5 * 60 * 1000) {
    const entry = await this.getById(id);
    if (!entry) return true;

    const age = Date.now() - entry.metadata.updatedAt;
    return age > staleTime;
  }
}

// Export repository instances
export const cacheMetadataRepository = new CacheMetadataRepository();
export const imageRepository = new ImageRepository();
export const syncQueueRepository = new SyncQueueRepository();

// Factory for entity repositories
export const createEntityRepository = (entityType) => {
  return new EntityRepository(entityType);
};

// Pre-configured entity repositories for common entities
export const athletesRepository = createEntityRepository('athletes');
export const trainersRepository = createEntityRepository('trainers');
export const programsRepository = createEntityRepository('programs');
export const dietsRepository = createEntityRepository('diets');
export const daybooksRepository = createEntityRepository('daybooks');
export const gymsRepository = createEntityRepository('gyms');
export const membershipsRepository = createEntityRepository('memberships');
export const settingsRepository = createEntityRepository('settings');
export const paymentsRepository = createEntityRepository('payments');
export const lockersRepository = createEntityRepository('lockers');
export const notificationsRepository = createEntityRepository('notifications');
export const exercisesRepository = createEntityRepository('exercises');
export const mealsRepository = createEntityRepository('meals');
